import cn from 'classnames'
import { useId, type FC, type ReactNode } from 'react'
import styles from './app-sidebar.module.css'

export interface SidebarSectionProps {
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Shown in the header while the section is closed, so its size stays visible. */
  count?: number
  /** Collapsed rail: the header becomes a divider and the content always shows. */
  rail: boolean
  children?: ReactNode
}

/** A collapsible sidebar section. The group keeps its name in the rail, where the header is a divider. */
export const SidebarSection: FC<SidebarSectionProps> = props => {
  const { title, open, onOpenChange, count, rail, children } = props
  const contentId = useId()
  const showContent = rail || open

  return (
    <div role="group" aria-label={title} className={styles.section}>
      {rail ? (
        <div className={styles.sectionDivider} aria-hidden="true" />
      ) : (
        <button
          type="button"
          className={styles.sectionHeader}
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => onOpenChange(!open)}
        >
          <span className={styles.grow}>{title}</span>
          {!open && count ? <span className={styles.count}>{count}</span> : null}
          <span className={cn(styles.chevron, !open && styles['chevron--closed'])} aria-hidden="true" />
        </button>
      )}
      <div id={contentId} hidden={!showContent} className={styles.sectionContent}>
        {children}
      </div>
    </div>
  )
}
